import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import { Button } from '../components/common/Button';

export interface CartItem {
  bookId: string;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  imageUrl: string;
  allowedCountries?: string[];
}

interface PendingItem {
  item: Omit<CartItem, 'quantity'>;
  quantity: number;
  onSuccess?: () => void;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity: number, onSuccess?: () => void) => void;
  removeFromCart: (bookId: string) => void;
  updateQuantity: (bookId: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
  getCartCurrency: () => string;
  getAllowedCountries: () => string[] | null;
  isLoadingSharedBook: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Modal styled components ──────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: modalIn 0.2s ease-out;

  @keyframes modalIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const ModalTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  margin: 0 0 12px 0;
`;

const ModalText = styled.p`
  font-size: 0.95rem;
  color: #495057;
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalCancelButton = styled.button`
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.95rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f8f9fa;
    border-color: #adb5bd;
  }
`;

// ── Provider ─────────────────────────────────────────────

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('shoppingCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isLoadingSharedBook, setIsLoadingSharedBook] = useState(false);
  const [pendingItem, setPendingItem] = useState<PendingItem | null>(null);

  useEffect(() => {
    localStorage.setItem('shoppingCart', JSON.stringify(items));
  }, [items]);

  const getAllowedCountries = useCallback((): string[] | null => {
    const restricted = items.filter(i => i.allowedCountries && i.allowedCountries.length > 0);
    if (restricted.length === 0) return null;

    let allowed = new Set<string>(restricted[0].allowedCountries!);
    for (let i = 1; i < restricted.length; i++) {
      const itemCountries = new Set(restricted[i].allowedCountries!);
      allowed = new Set([...allowed].filter(c => itemCountries.has(c)));
    }
    return [...allowed];
  }, [items]);

  const hasCountryConflict = useCallback((newItem: Omit<CartItem, 'quantity'>): boolean => {
    if (items.length === 0) return false;

    const newCountries = newItem.allowedCountries && newItem.allowedCountries.length > 0
      ? newItem.allowedCountries
      : null;
    const cartCountries = getAllowedCountries();

    // No conflict if either side ships everywhere
    if (!newCountries || !cartCountries) return false;

    const overlap = newCountries.some(c => cartCountries.includes(c));
    return !overlap;
  }, [items, getAllowedCountries]);

  const doAddToCart = useCallback((item: Omit<CartItem, 'quantity'>, quantity: number) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(i => i.bookId === item.bookId);
      if (existingItem) {
        return prevItems.map(i =>
          i.bookId === item.bookId
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      } else {
        return [...prevItems, { ...item, quantity }];
      }
    });
  }, []);

  const addToCart = useCallback((item: Omit<CartItem, 'quantity'>, quantity: number, onSuccess?: () => void) => {
    if (hasCountryConflict(item)) {
      setPendingItem({ item, quantity, onSuccess });
      return;
    }

    doAddToCart(item, quantity);
    onSuccess?.();
  }, [hasCountryConflict, doAddToCart]);

  const handleReplaceCart = useCallback(() => {
    if (!pendingItem) return;
    setItems([{ ...pendingItem.item, quantity: pendingItem.quantity }]);
    pendingItem.onSuccess?.();
    setPendingItem(null);
  }, [pendingItem]);

  const handleKeepCart = useCallback(() => {
    setPendingItem(null);
  }, []);

  const removeFromCart = useCallback((bookId: string) => {
    setItems(prevItems => prevItems.filter(item => item.bookId !== bookId));
  }, []);

  const updateQuantity = useCallback((bookId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(bookId);
      return;
    }
    setItems(prevItems =>
      prevItems.map(item =>
        item.bookId === bookId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  const getCartCurrency = useCallback(() => {
    return items.length > 0 ? items[0].currency : 'EUR';
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getItemCount,
        getCartCurrency,
        getAllowedCountries,
        isLoadingSharedBook,
      }}
    >
      {children}

      {pendingItem && (
        <ModalOverlay onClick={handleKeepCart}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>{t('bookCarousel.conflictTitle')}</ModalTitle>
            <ModalText>{t('bookCarousel.conflictMessage')}</ModalText>
            <ModalButtons>
              <Button variant="primary" size="medium" onClick={handleReplaceCart}>
                {t('bookCarousel.replaceCart')}
              </Button>
              <ModalCancelButton onClick={handleKeepCart}>
                {t('bookCarousel.keepCart')}
              </ModalCancelButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const useSharedBookLink = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart, items } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const bookId = searchParams.get('bookId');
    if (!bookId) return;

    const alreadyInCart = items.some(item => item.bookId === bookId);
    if (alreadyInCart) {
      searchParams.delete('bookId');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (fetchingRef.current) return;
    fetchingRef.current = true;

    const fetchAndAddBook = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/books/${bookId}`);
        if (!response.ok) throw new Error('Book not found');

        const book = await response.json();

        addToCart(
          {
            bookId: book.id,
            title: book.title,
            price: book.price,
            currency: book.currency,
            imageUrl: book.imageUrl,
            allowedCountries: book.allowedCountries || [],
          },
          1,
          () => {
            searchParams.delete('bookId');
            setSearchParams(searchParams, { replace: true });
          }
        );
      } catch (err) {
        console.error('Failed to load shared book:', err);
        setError('Could not load the book. It may no longer be available.');
      } finally {
        setIsLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchAndAddBook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return { isLoading, error };
};